import React, { useContext, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  FormHelperText,
  List,
  ListItem,
  Typography,
  useTheme,
  withWidth,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import Helmet from 'react-helmet';
import _ from 'lodash';
import clsx from 'clsx';
import { Info } from '@material-ui/icons';
import Grid from '@material-ui/core/Grid';
import colors from '../../../themes/main-colors';

import { navigate } from '@reach/router';
import ROUTES from '../../../routes';

import {
  AccountCustomer,
  AccountDetail,
  AccountType,
  IqbdEnrollIneligibilityReason,
  IqbdEnrollInput,
  IqbdOperations,
  PremiseInfo,
} from '../../../__generated__/pge-types';
import useUtil from '../../need-more-time-to-pay/useUtil';
import useWrapWithLoader from '../../../hooks/useWrapWithLoading';
import useIQBDForm from '../useIQBDForm';
import { IQBDModel } from '../IQBDForm.rules';

import CurrencyTextField from '../../currency-text-field';
import Page2ColumnLayout from '../../utility/page2-column-layout';
import { SelectDropdown } from './SelectDropdown';
import { RadioOption } from './RadioOption';
import TextField from '../../text-field';
import { HouseholdIncomeGuidelines } from './HouseholdIncomeGuidelines';
import IQBDHeaderCard, { IQBD_HEADER_CARD_TYPES } from '../IQBDHeaderCard';
import Backdrop from '../../backdrop';
import { C2MPreferredLanguages } from '../../../util/languageUtil';
import iqbdFormStateAdapter from '../utils/iqbdFormStateAdapter';
import { FormTopNavbar } from '../FormTopNavbar';
import useAuthQuery from '../../../hooks/useAuthQuery';
import useContentMgmt from '../../../hooks/useContentMgmt';
import { getAccountInfoForIQBD, getIQBDEligibility } from '../queries';
import { formatFullDateWithYear } from '../../../util/date-utils';
import { LanguageContext } from '../../../providers/LanguageProvider';
import { CustomerDetails } from '../CustomerDetails';
import useSelectedAccountParams from '../../../hooks/useSelectedAccountParams';
import useAccountCustomer from '../../../hooks/useAccountCustomer';
import AccountLeftNavBlock from '../../utility/account-left-nav-block';

interface FormPageProps {
  children?: React.ReactNode;
  path?: string;
  isShowform?: any;
}

export enum FORM_TYPE_ACTION_BUTTON_TYPES {
  Update = 'update',
  Unenroll = 'unenroll',
}

export enum FORM_TYPE {
  Self = 'self',
  Other = 'other',
  Public = 'public',
}

const useStyles = makeStyles(() => ({
  applyingForBox: {
    border: `1px solid ${colors.lightGray}`,
    backgroundColor: colors.white,
    borderRadius: '16px',
    padding: '48px 0',
    width: '333px',
    textAlign: 'center',
    fontSize: '18px',
    cursor: 'pointer',
    fontFamily: 'Untitled-Sans',
    transition: 'all 0.2s',
  },
  selectedApplyingForBox: {
    background: colors.riverBlue,
    boxShadow: '0px 3px 6px #0000004D',
    fontWeight: 500,
  },
  miniApplyingForBox: {
    padding: '12px',
  },
  formSection: {
    background: `${colors.white} 0% 0% no-repeat padding-box`,
    borderRadius: '5px',
    padding: '30px 20px',
    margin: '10px 0',
  },
  sectionHeading: {
    fontFamily: 'Forma-DJR-Display',
    fontWeight: 'bold',
    fontSize: '24px',
    lineHeight: '28px',
    letterSpacing: '0.48px',
    color: colors.lightCyan,
    marginBottom: '15px',
  },
  sectionInfo: {
    fontFamily: 'Untitled-Sans',
    fontSize: '16px',
    lineHeight: '20px',
    color: colors.lightCyan,
    display: 'block',
    margin: '5px 0',
    '&:last-child': {
      marginBottom: 0,
    },
  },
  highlighted: {
    textDecoration: 'underline',
    fontWeight: 'bold',
    color: colors.sparkBlue,
    cursor: 'pointer',
    fontSize: 'inherit',
    lineHeight: 'inherit',
  },
  button: {
    borderRadius: '5px',
    padding: '13px 57px',
    fontWeight: 'bold',
    fontSize: '18px',
    fontFamily: 'Untitled-Sans',
    display: 'block',
    '&:hover': {
      boxShadow: 'none',
    },
  },
  listItem: {
    display: 'list-item',
    padding: 0,
    fontSize: '16px',
    lineHeight: '20px',
    fontFamily: 'Untitled-Sans',
    margin: '8px 0',
  },
}));

const IQBD_FORM_MICROGROUP_COPY_ID = '6wxqSYOBj1ybyYgyRPeQOA';

const Form: React.FC<FormPageProps> = props => {
  console.log('props', props);
  const theme = useTheme();
  const { wrapWithLoader } = useWrapWithLoader();
  const { setErrorNotification } = useUtil();
  const { content, richText } = useContentMgmt(IQBD_FORM_MICROGROUP_COPY_ID);
  const { language } = useContext(LanguageContext);
  const classes = useStyles();

  const [applyingFor, setApplyingFor] = useState<string>(FORM_TYPE.Self);
  const [submitTab, setSubmitTab] = useState<string>(applyingFor);
  const [needHelpToCalculateIncome, setNeedHelpToCalculateIncome] = useState<
    boolean
  >(false);
  const [
    showHouseholdIncomeGuidelines,
    setShowHouseholdIncomeGuidelines,
  ] = useState<boolean>(false);
  const [selectedAccount, setSelectedAccount] = useState<
    AccountDetail | undefined
  >(undefined);
  const [premiseId, setPremiseId] = useState<string>('');
  const [ismultiPremiseId, setIsmultiPremiseId] = useState<boolean>(false);

  const [premise, setPremise] = useState<PremiseInfo | undefined>(undefined);
  const [loading, setLoading] = useState<boolean>(false);
  const [showForm, setShowForm] = useState<boolean>(props?.isShowform || false);
  const [headercardData, setHeadercardData] = useState<any>(null);
  const [income, setIncome] = useState<any>(null);
  const [incomePeriod, setIncomePeriod] = useState<string>('Monthly');
  const [showAccountSelectDropdown, setShowAccountSelectDropdown] = useState<
    boolean
  >(true);
  const [formTypeActionButtonTypes, setFormTypeActionButtonTypes] = useState<
    FORM_TYPE_ACTION_BUTTON_TYPES[]
  >([]);

  const { data: customerData, loading: customerLoading } = useAuthQuery<{
    getAccountInfo: AccountCustomer;
  }>(getAccountInfoForIQBD, {
    variables: {
      params: {
        removeInactiveAccounts: true,
      },
    },
    fetchPolicy: 'no-cache',
  });

  const customer = customerData?.getAccountInfo;

  const {
    data: iqbdEligibilityDetail,
    loading: iqbdEligibilityLoading,
  } = useAuthQuery<{ getAccountDetails: AccountDetail[] }>(getIQBDEligibility, {
    variables: {
      iqbdParams: {
        encryptedSaIds: premise?.saDetails?.map(
          saDetail => saDetail?.encryptedSAId,
        ),
      },
      params: {
        accountNumberList: [{ accountNumber: selectedAccount?.accountNumber }],
      },
    },
    skip: !(
      premise &&
      selectedAccount &&
      selectedAccount.accountType === AccountType.Res
    ),
    fetchPolicy: 'no-cache',
  });

  const { form, unEnrollIqbd, submitForm } = useIQBDForm(content, applyingFor);

  const { encryptedPersonId } = useSelectedAccountParams({
    ignoreNoAccounts: true,
  });

  const { customer: user } = useAccountCustomer();

  useEffect(() => {
    if (selectedAccount && selectedAccount.accountType !== AccountType.Res) {
      setHeadercardData({
        type: IQBD_HEADER_CARD_TYPES.Ineligible,
        reason: IqbdEnrollIneligibilityReason.CustNotRes,
      });
    }
  }, [selectedAccount]);

  useEffect(() => {
    if (iqbdEligibilityDetail) {
      setShowForm(true);
      setFormTypeActionButtonTypes([]);
      void getIqbdEnrollStatus();
    }
  }, [iqbdEligibilityDetail]);

  useEffect(() => {
    if (!customerLoading && customer && !form.values.prefLanguage) {
      void form.setValue(
        'prefLanguage',
        _.capitalize(customer?.prefLanguage?.toLowerCase() || 'English'),
      );
    }
  }, [customerLoading]);

  const getIqbdEnrollStatus = async () => {
    const data = iqbdEligibilityDetail?.getAccountDetails[0];
    const selectedPremise = data?.premiseInfo?.find(
      item =>
        item?.addressLine1 === premise?.addressLine1 &&
        item?.addressLine2 === premise?.addressLine2 &&
        item?.city === premise?.city &&
        item?.county === premise?.county &&
        item?.state === premise?.state &&
        item?.postal === premise?.postal &&
        item?.country === premise?.country,
    );
    if (data?.IQBDStatus?.isEnrolled) {
      if (data.IQBDStatus.isSameAccountEnrolled) {
        setHeadercardData({
          type: IQBD_HEADER_CARD_TYPES.Enrolled,
          discount: data.IQBDStatus.discountTier?.replace('PCT', '%'),
          expirationDate:
            data.IQBDStatus.expirationDate &&
            formatFullDateWithYear(
              new Date(data.IQBDStatus.expirationDate),
              language,
              true,
            ),
        });
        setShowForm(false);
        setFormTypeActionButtonTypes([
          FORM_TYPE_ACTION_BUTTON_TYPES.Update,
          FORM_TYPE_ACTION_BUTTON_TYPES.Unenroll,
        ]);
      } else {
        setHeadercardData({
          type: IQBD_HEADER_CARD_TYPES.EnrolledFromAnotherAccount,
        });
        setShowForm(false);
      }
    } else if (data?.IQBDStatus?.isPendingReview) {
      setHeadercardData({ type: IQBD_HEADER_CARD_TYPES.PendingReview });
      setShowForm(false);
    } else if (data?.IQBDStatus?.isExpired) {
      setHeadercardData({ type: IQBD_HEADER_CARD_TYPES.Expired });
    } else {
      setLoading(true);
      if (
        selectedPremise?.IQBDEligibility?.eligibility &&
        !selectedPremise.IQBDEligibility.eligibility?.isEligible
      ) {
        const cardData = {
          type: IQBD_HEADER_CARD_TYPES.IneligibleFromPrevApplication,
          reason: selectedPremise?.IQBDEligibility?.eligibility?.reason,
        };
        if (
          selectedPremise?.IQBDEligibility?.eligibility?.reason !==
          IqbdEnrollIneligibilityReason.Overincome
        ) {
          setShowForm(false);
          cardData.type = IQBD_HEADER_CARD_TYPES.Ineligible;
        }
        setHeadercardData(cardData);
      } else {
        setHeadercardData(null);
      }
      setLoading(false);
    }
  };

  const handleOnClickApplyingFor = (value: string) => {
    setApplyingFor(value);
    setShowForm(true);
    form.reset();
    if (value === FORM_TYPE.Other) {
      setHeadercardData(null);
      setFormTypeActionButtonTypes([]);
    } else {
      void getIqbdEnrollStatus();
    }
  };

  const handleUnEnroll = wrapWithLoader(async () => {
    try {
      const { data: unEnrollResult } = await unEnrollIqbd({
        accountNumber: selectedAccount?.accountNumber!,
        encryptedPersonId: selectedAccount?.encryptedPersonId!,
        encryptedPremiseId: premise?.encryptedPremiseId!,
        unEnrollReason: 'Customer Unenrolled',
      });

      if (!unEnrollResult?.IQBDUnenroll.isUnenrolled) {
        setErrorNotification(true);
        return;
      }

      setHeadercardData({ type: IQBD_HEADER_CARD_TYPES.UnEnrolled });
      setShowForm(false);

      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    } catch (e) {
      console.log(e);
      setErrorNotification(true);
    }
  });

  const handleSubmit = wrapWithLoader(async (data: IQBDModel) => {
    if (ismultiPremiseId) {
      setShowForm(false);
      setHeadercardData({
        type: IQBD_HEADER_CARD_TYPES.Ineligible,
        reason: IqbdEnrollIneligibilityReason.MultiPremise,
      });
    } else {
      try {
        const enrollAction: IqbdOperations =
          (selectedAccount?.IQBDStatus?.isEnrolled &&
            applyingFor === FORM_TYPE.Self) ||
          (headercardData?.type === IQBD_HEADER_CARD_TYPES.Success &&
            applyingFor === FORM_TYPE.Self) ||
          (headercardData?.type === IQBD_HEADER_CARD_TYPES.Enrolled &&
            applyingFor === FORM_TYPE.Self)
            ? IqbdOperations.Update
            : IqbdOperations.Enroll;
        const formData: IqbdEnrollInput = iqbdFormStateAdapter.fromFormState(
          content,
          data,
          applyingFor === FORM_TYPE.Self
            ? selectedAccount?.accountNumber! || ''
            : data.accountNumber || '',
          applyingFor === FORM_TYPE.Self
            ? premise?.encryptedPremiseId!
            : premiseId,
          applyingFor === FORM_TYPE.Self
            ? selectedAccount?.encryptedPersonId!
            : encryptedPersonId!,
          selectedAccount?.accountNumber,
          premise?.encryptedPremiseId,
        );
        setSubmitTab(applyingFor);
        const result = await submitForm({ ...formData, action: enrollAction });
        if (result?.data?.IQBDEnroll.isEnrolled) {
          setShowForm(false);
          setHeadercardData({
            type: IQBD_HEADER_CARD_TYPES.Success,
            discount: result.data.IQBDEnroll.discountPercentage?.replace(
              'PCT',
              '%',
            ),
          });
        } else {
          if (
            result?.data?.IQBDEnroll.reason !==
            IqbdEnrollIneligibilityReason.Overincome
          ) {
            setShowForm(false);
          } else {
            setShowForm(false);
            setFormTypeActionButtonTypes([]);
          }
          setHeadercardData({
            type: IQBD_HEADER_CARD_TYPES.Ineligible,
            reason: result?.data?.IQBDEnroll.reason,
          });
        }
        window.scrollTo({
          top: 0,
          behavior: 'smooth',
        });
      } catch (e) {
        console.log(e);
        setErrorNotification(true);
      }
    }
  });

  return (
    <>
      {(loading || iqbdEligibilityLoading) && <Backdrop forceOpen />}
      <form onSubmit={form.submit(values => handleSubmit(values))}>
        <Helmet>
          <title>{content.get('IQBD_PAGE_TITLE')}</title>
        </Helmet>
        <Page2ColumnLayout leftColumn={<AccountLeftNavBlock />}>
          <Grid container direction="column" spacing={2}>
            <Grid item>
              <Typography
                css={{
                  fontFamily: 'Forma-DJR-Display',
                  letterSpacing: '0.72px',
                  color: colors.lightCyan,
                  fontSize: '36px',
                  lineHeight: '36px',
                  fontWeight: 'bold',
                }}
              >
                {content.get('PAGE_TITLE')}
              </Typography>
            </Grid>
            <Grid
              container
              direction="row"
              justify="space-between"
              css={{ margin: '32px 0' }}
            >
              <Box
                className={clsx(
                  classes.applyingForBox,
                  applyingFor === FORM_TYPE.Self &&
                    classes.selectedApplyingForBox,
                  applyingFor && classes.miniApplyingForBox,
                )}
                onClick={() => handleOnClickApplyingFor(FORM_TYPE.Self)}
              >
                {content.get('APPLYING_FOR_YOURSELF')}
              </Box>
              <Box
                className={clsx(
                  classes.applyingForBox,
                  applyingFor === FORM_TYPE.Other &&
                    classes.selectedApplyingForBox,
                  applyingFor && classes.miniApplyingForBox,
                )}
                onClick={() => handleOnClickApplyingFor(FORM_TYPE.Other)}
              >
                {content.get('APPLYING_FOR_SOMEONE_ELSE')}
              </Box>
            </Grid>
            {applyingFor === FORM_TYPE.Self ? (
              showAccountSelectDropdown ? (
                <FormTopNavbar
                  content={content}
                  selectedAccount={selectedAccount}
                  selectedPremise={premise}
                  onSelectAccount={(
                    selectedAccountDetail: AccountDetail,
                    shouldHideAccountSelection?: boolean,
                  ) => {
                    setSelectedAccount(selectedAccountDetail);
                    if (selectedAccountDetail.premiseInfo?.length) {
                      setPremise(selectedAccountDetail.premiseInfo[0]!);
                    }
                    if (shouldHideAccountSelection) {
                      setShowAccountSelectDropdown(false);
                    }
                  }}
                  onSelectPremise={(selectedPremise: PremiseInfo) => {
                    setPremise(selectedPremise);
                  }}
                />
              ) : (
                <Grid
                  item
                  css={{
                    margin: '16px 0 10px 0',
                    padding: '12px 18px',
                    border: `1px solid ${colors.lightGray1}`,
                    backgroundColor: colors.white,
                    borderRadius: '4px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Typography
                    css={{
                      fontSize: '13px',
                      color: colors.darkSlateBlue,
                    }}
                  >
                    {selectedAccount?.accountNumber}
                    {selectedAccount?.description
                      ? ` - ${selectedAccount?.description}`
                      : null}
                  </Typography>
                  <Typography
                    css={{
                      fontSize: '13px',
                      color: colors.darkSlateBlue,
                    }}
                  >
                    {premise?.addressLine1} {premise?.addressLine2}
                  </Typography>
                </Grid>
              )
            ) : null}
            {headercardData && (
              <IQBDHeaderCard
                content={content}
                {...headercardData}
                formTypeActionButtonTypes={formTypeActionButtonTypes}
                handleUpdate={() => {
                  setShowForm(true);
                }}
                handleUnEnroll={handleUnEnroll}
                onClickDone={() => {
                  setHeadercardData(null);
                  setShowForm(true);
                }}
                selectedTab={applyingFor}
              />
            )}
            {(headercardData?.type === IQBD_HEADER_CARD_TYPES.Success ||
              (!showForm &&
                iqbdEligibilityDetail &&
                formTypeActionButtonTypes.length === 0) ||
              (selectedAccount &&
                selectedAccount.accountType !== AccountType.Res)) && (
              <Button
                data-testid={'iqbd-form-success'}
                variant={'outlined'}
                color={'primary'}
                css={{
                  backgroundColor: colors.buttonHoverPrimary,
                  borderColor: colors.buttonHoverPrimary,
                  color: colors.white,
                  borderRadius: '30px',
                  fontSize: '18px',
                  minWidth: '130px',
                  paddingLeft: '64px',
                  paddingRight: '64px',
                  margin: '0 auto 30px',
                  '&:hover': {
                    backgroundColor: colors.buttonHoverPrimary,
                    borderColor: colors.buttonHoverPrimary,
                    color: colors.white,
                    boxShadow: 'none',
                  },
                }}
                onClick={() => navigate(ROUTES.ACCOUNT)}
              >
                {headercardData?.type === IQBD_HEADER_CARD_TYPES.Success
                  ? content.get('DONE')
                  : content.get('BACK_TO_MY_ACCOUNT')}
              </Button>
            )}
            {showForm && (
              <>
                <Grid
                  item
                  css={{
                    boxShadow: '0px 3px 6px #0000004D',
                    borderRadius: '4px',
                    backgroundColor: colors.riverBlue,
                    padding: '20px !important',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <Box>
                    <Typography
                      css={{
                        font: 'Untitled-Sans',
                        color: colors.lightCyan,
                        fontSize: '18px',
                        lineHeight: '20px',
                        marginBottom: '20px',
                      }}
                    >
                      {richText(content.get('FORM_DESCRIPTION'))}
                    </Typography>
                  </Box>
                  {applyingFor === FORM_TYPE.Self ? (
                    <Box className={classes.formSection}>
                      <Typography className={classes.sectionHeading}>
                        {content.get('ACCOUNT_HOLDER_INFO')}
                      </Typography>
                      <Typography className={classes.sectionInfo}>
                        {selectedAccount?.mainCustomerName}
                      </Typography>
                      <Typography className={classes.sectionInfo}>
                        {content.get('ACCOUNT_NUMBER')}:{' '}
                        {selectedAccount?.accountNumber}
                        {selectedAccount?.description
                          ? ` - ${selectedAccount?.description}`
                          : null}
                      </Typography>
                      <Typography className={classes.sectionInfo}>
                        {content.get('PRIMARY_ADDRESS')}:{' '}
                        {premise?.addressLine1} {premise?.addressLine2}
                      </Typography>
                    </Box>
                  ) : (
                    <Box className={classes.formSection}>
                      <Typography className={classes.sectionHeading}>
                        {content.get('ACCOUNT_HOLDER_INFO')}
                      </Typography>
                      <Typography className={classes.sectionInfo}>
                        {user?.personName}
                      </Typography>
                      <Typography className={classes.sectionInfo}>
                        {user?.email}
                      </Typography>
                    </Box>
                  )}
                  {applyingFor === FORM_TYPE.Other ? (
                    <Box className={classes.formSection}>
                      <CustomerDetails
                        form={form}
                        setPremiseId={setPremiseId}
                        setIsmultiPremiseId={setIsmultiPremiseId}
                      />
                    </Box>
                  ) : null}

                  <Box className={classes.formSection}>
                    <Typography className={classes.sectionHeading}>
                      {content.get('HOUSEHOLD_INCOME_INFO_HEADING')}
                    </Typography>
                    <Typography
                      className={classes.sectionInfo}
                      css={{ margin: 0 }}
                    >
                      {content.get('HOUSEHOLD_INCOME_DESC')}
                    </Typography>
                    <Typography
                      className={classes.sectionInfo}
                      css={{ margin: '16px 0 30px 0' }}
                    >
                      {content.get('HOUSEHOLD_INCOME_UNSURE_TEXT_1')}{' '}
                      <Typography
                        component="span"
                        className={classes.highlighted}
                        onClick={() => setShowHouseholdIncomeGuidelines(true)}
                      >
                        {content.get('HOUSEHOLD_INCOME_UNSURE_TEXT_2')}
                      </Typography>
                      .
                    </Typography>

                    <Box mb={'20px'}>
                      <SelectDropdown
                        title={content.get('HOUSEHOLD_SIZE_TITLE')}
                        selectedValue={
                          form.values.houseHoldSize
                            ? form.values.houseHoldSize.toString()
                            : ''
                        }
                        placeholder={content.get('PLEASE_SELECT')}
                        options={Array.from({ length: 15 }, (__, i) => i + 1)}
                        onChange={value => {
                          void form.setValue('houseHoldSize', value);
                        }}
                      />
                      {form.errors.houseHoldSize && (
                        <FormHelperText error={true}>
                          {form.errors.houseHoldSize}
                        </FormHelperText>
                      )}
                    </Box>

                    <Box
                      mb={'10px'}
                      css={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '5px',
                      }}
                    >
                      <Typography
                        css={{
                          fontSize: '16px',
                          lineHeight: '20px',
                          color: colors.lightCyan,
                          display: 'block',
                        }}
                      >
                        {richText(content.get('NEED_HELP_CALCULATING_INCOME'))}
                      </Typography>
                      <Info
                        css={{ cursor: 'pointer' }}
                        onClick={() =>
                          setNeedHelpToCalculateIncome(
                            !needHelpToCalculateIncome,
                          )
                        }
                      />
                    </Box>

                    {needHelpToCalculateIncome && (
                      <Box
                        css={{
                          background: colors.cloudGrey,
                          borderRadius: '5px',
                          padding: '30px 27px',
                          marginBottom: '16px',
                        }}
                      >
                        <Typography
                          className={classes.sectionInfo}
                          css={{ marginBottom: '20px' }}
                        >
                          {content.get('CALCULATE_INCOME_TEXT_1')}
                        </Typography>

                        <Typography
                          className={classes.sectionInfo}
                          css={{ margin: '20px 0 30px 0' }}
                        >
                          {content.get('CALCULATE_INCOME_TEXT_6')}
                        </Typography>

                        <Typography className={classes.sectionInfo}>
                          {content.get('CALCULATE_INCOME_TEXT_7')}{' '}
                          <Typography
                            component="span"
                            className={classes.highlighted}
                          >
                            {content.get('ASSISTANCE_PHONE_NUMBER')}
                          </Typography>
                          .
                        </Typography>
                      </Box>
                    )}

                    <Grid
                      xs={12}
                      container
                      css={{
                        marginBottom: '30px',
                      }}
                    >
                      <Typography className={classes.sectionInfo}>
                        {content.get('AVG_GROSS_ANNUAL_INCOME')}
                      </Typography>

                      <Grid
                        container
                        item
                        spacing={2}
                        css={{ marginTop: '8px' }}
                      >
                        <Grid item xs={5} md={3}>
                          <CurrencyTextField
                            css={{
                              margin: theme.spacing(0, 0, 0, 0),
                              '& label': {
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                width: '90%',
                                fontSize: '16px',
                                transform: 'translate(14px, 16px) scale(1)',
                                '&.MuiInputLabel-shrink': {
                                  overflow: 'unset',
                                },
                              },
                              '& input': {
                                height: '48px',
                                fontSize: '16px',
                                padding: '0 14px',
                              },
                            }}
                            label={content.get('INCOME')}
                            placeholder={'income'}
                            onChange={(e: any) => {
                              const inputVal = Number(e.target.value);
                              setIncome(inputVal);
                              let calculatedIncome = null;
                              if (incomePeriod === content.get('MONTHLY')) {
                                calculatedIncome = inputVal * 12;
                              } else if (
                                incomePeriod === content.get('WEEKLY')
                              ) {
                                calculatedIncome = inputVal * 52;
                              } else if (
                                incomePeriod === content.get('BIWEEKLY')
                              ) {
                                calculatedIncome = inputVal * 26;
                              } else {
                                calculatedIncome = inputVal;
                              }
                              void form.setValue(
                                'annualIncome',
                                calculatedIncome.toString(),
                              );
                            }}
                            error={false}
                          />
                        </Grid>
                        <Grid
                          item
                          xs={2}
                          md={1}
                          css={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                          }}
                        >
                          <Typography className={classes.sectionInfo}>
                            {content.get('PER')}
                          </Typography>
                        </Grid>
                        <Grid item xs={5} md={2}>
                          <SelectDropdown
                            isWidth={false}
                            selectedValue={incomePeriod}
                            placeholder={content.get('PLEASE_SELECT')}
                            options={[
                              content.get('WEEKLY'),
                              content.get('BIWEEKLY'),
                              content.get('MONTHLY'),
                              content.get('ANNUAL'),
                            ]}
                            onChange={value => {
                              setIncomePeriod(value);
                              let calculatedIncome = null;
                              if (incomePeriod !== value) {
                                if (value === content.get('MONTHLY')) {
                                  calculatedIncome = income * 12;
                                } else if (value === content.get('WEEKLY')) {
                                  calculatedIncome = income * 52;
                                } else if (value === content.get('BIWEEKLY')) {
                                  calculatedIncome = income * 26;
                                } else {
                                  calculatedIncome = income;
                                }
                              }
                              void form.setValue(
                                'annualIncome',
                                calculatedIncome,
                              );
                            }}
                          />
                        </Grid>
                      </Grid>

                      {form.errors.annualIncome && (
                        <FormHelperText error={true}>
                          {form.errors.annualIncome}
                        </FormHelperText>
                      )}

                      {incomePeriod && (
                        <Typography
                          style={{ marginTop: '16px', fontWeight: 500 }}
                        >
                          {content.get('CALCULATED_INCOME')} = $
                          {(
                            Number(form.values.annualIncome) || 0
                          ).toLocaleString()}
                        </Typography>
                      )}
                    </Grid>

                    <Box
                      mb={'30px'}
                      css={
                        form.errors.confirmAnnualIncome
                          ? {
                              '&>div>p:first-child': {
                                color: colors.errorRed,
                              },
                            }
                          : {}
                      }
                    >
                      <FormControlLabel
                        control={
                          <Checkbox
                            color={
                              form.values.confirmAnnualIncome
                                ? 'primary'
                                : 'default'
                            }
                            size={'medium'}
                            css={{ padding: '0' }}
                          />
                        }
                        checked={Boolean(form.values.confirmAnnualIncome)}
                        onChange={() =>
                          form.setValue(
                            'confirmAnnualIncome',
                            !form.values.confirmAnnualIncome,
                          )
                        }
                        label={
                          <Typography
                            css={{
                              fontSize: '16px',
                              marginLeft: '8px',
                              color: form.errors.confirmAnnualIncome
                                ? colors.errorRed
                                : colors.lightCyan,
                            }}
                          >
                            {content.get('CONFIRM_ANNUAL_INCOME')}
                          </Typography>
                        }
                        css={{
                          display: 'flex',
                          alignItems: 'center',
                          marginLeft: 0,
                        }}
                      />
                    </Box>

                    <Box mb={'30px'}>
                      <RadioOption
                        title={content.get('FIXED_INCOME')!}
                        yesOptionTitle={content.get('REUSABLE_YES')}
                        noOptionTitle={content.get('REUSABLE_NO')}
                        selectedValue={form.values.fixedIncome}
                        onChange={value => {
                          void form.setValue('fixedIncome', value);
                        }}
                      />
                      {form.errors.fixedIncome && (
                        <FormHelperText error={true}>
                          {form.errors.fixedIncome}
                        </FormHelperText>
                      )}
                    </Box>

                    <Box>
                      <Box
                        mb={
                          form.values.ethinicity === content.get('OTHER')
                            ? '0px'
                            : '40px'
                        }
                      >
                        <SelectDropdown
                          title={content.get('ETHNICITY_TEXT')}
                          selectedValue={form.values.ethinicity}
                          placeholder={content.get('PLEASE_SELECT')}
                          options={[
                            content.get('ETHNICITY_1'),
                            content.get('ETHNICITY_2'),
                            content.get('ETHNICITY_3'),
                            content.get('ETHNICITY_4'),
                            content.get('ETHNICITY_5'),
                            content.get('ETHNICITY_6'),
                            content.get('ETHNICITY_7'),
                            content.get('ETHNICITY_8'),
                            content.get('ETHNICITY_9'),
                            content.get('OTHER'),
                            content.get('PREFER_NOT_TO_RESPOND'),
                          ]}
                          onChange={value => {
                            void form.setValue('ethinicity', value);
                          }}
                        />
                        {form.errors.ethinicity && (
                          <FormHelperText error={true}>
                            {form.errors.ethinicity}
                          </FormHelperText>
                        )}
                      </Box>
                    </Box>

                    {form.values.ethinicity === 'Other' && (
                      <Box mb={'40px'}>
                        <TextField
                          name={content.get('OTHER_ETHNICITY')}
                          label={content.get('OTHER_ETHNICITY')}
                          value={form.values.otherEthnicity}
                          onChange={(e: any) => {
                            void form.setValue(
                              'otherEthnicity',
                              e.target.value,
                            );
                          }}
                          css={{
                            margin: theme.spacing(2, 0, 0, 0),
                            width: '315px !important',
                            '& label': {
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              width: '90%',
                              fontSize: '16px',
                              transform: 'translate(14px, 16px) scale(1)',
                              '&.MuiInputLabel-shrink': {
                                overflow: 'unset',
                              },
                            },
                            '& input': {
                              height: '48px',
                              fontSize: '16px',
                              padding: '0 14px',
                            },
                          }}
                        />
                        {form.errors.otherEthnicity && (
                          <FormHelperText error={true}>
                            {form.errors.otherEthnicity}
                          </FormHelperText>
                        )}
                      </Box>
                    )}
                    <Box>
                      <Box mb={'40px'}>
                        <SelectDropdown
                          title={content.get('PREFERRED_CONTACT_LANGUAGE')}
                          placeholder={content.get('PLEASE_SELECT')}
                          selectedValue={form.values.prefLanguage}
                          options={Object.keys(C2MPreferredLanguages)}
                          onChange={value => {
                            void form.setValue('prefLanguage', value);
                          }}
                        />
                        {form.errors.prefLanguage && (
                          <FormHelperText error={true}>
                            {form.errors.prefLanguage}
                          </FormHelperText>
                        )}
                      </Box>
                    </Box>

                    {!selectedAccount?.isPaperlessBillEnrolled?.result &&
                      applyingFor === FORM_TYPE.Self && (
                        <Box
                          mb={'30px'}
                          css={{
                            '& > div  div': {
                              display: 'flex',
                              flexDirection: 'row',
                              gap: '16px',
                            },
                          }}
                        >
                          <RadioOption
                            title={content.get('ENROLLED_IN_PAPERLESS')!}
                            yesOptionTitle={content.get('SWITCH_TO_PAPERLESS')}
                            noOptionTitle={content.get('MAIL')}
                            selectedValue={
                              form.values.paperless
                                ? content.get('REUSABLE_YES')
                                : content.get('REUSABLE_NO')
                            }
                            onChange={value => {
                              void form.setValue(
                                'paperless',
                                value === content.get('REUSABLE_YES'),
                              );
                            }}
                          />
                          <Typography
                            component="span"
                            css={{
                              display: 'block',
                              marginTop: '8px',
                              fontSize: ' 0.8rem',
                            }}
                          >
                            {content.get('PAPERLESS_DESC')}
                          </Typography>
                          {form.errors.paperless && (
                            <FormHelperText error={true}>
                              {form.errors.paperless}
                            </FormHelperText>
                          )}
                        </Box>
                      )}
                  </Box>

                  <Box className={classes.formSection}>
                    <Typography className={classes.sectionHeading}>
                      {content.get('DECLARATION')}
                    </Typography>
                    <List
                      component="ol"
                      className={classes.sectionInfo}
                      css={{
                        padding: '20px 20px 0 20px',
                        listStyleType: 'decimal',
                        listStyle: 'auto',
                      }}
                    >
                      <ListItem component="li" className={classes.listItem}>
                        {content.get('DECLARATION_TEXT_1')}
                      </ListItem>
                      <ListItem component="li" className={classes.listItem}>
                        {content.get('DECLARATION_TEXT_2')}
                      </ListItem>
                      <ListItem component="li" className={classes.listItem}>
                        {content.get('DECLARATION_TEXT_3')}
                      </ListItem>
                    </List>
                    <Typography
                      className={classes.sectionInfo}
                      css={{ margin: '20px 0' }}
                    >
                      {richText(
                        content.get('ELECTRONIC_SIGNATURE_DECLARATION'),
                      )}
                    </Typography>
                    <Typography
                      css={{
                        fontFamily: 'Forma-DJR-Display',
                        fontSize: '18px',
                        lineHeight: '28px',
                        fontWeight: 'bold',
                        letterSpacing: '0.36px',
                        color: colors.lightCyan,
                      }}
                    >
                      {content.get('ELECTRONIC_SIGNATURE')}
                    </Typography>

                    <TextField
                      name={content.get('ENTER_FULL_NAME')}
                      label={content.get('ENTER_FULL_NAME')}
                      value={form.values.signature}
                      onChange={(e: any) => {
                        void form.setValue('signature', e.target.value);
                      }}
                      css={{
                        margin: theme.spacing(2, 0, 0, 0),
                        width: '315px !important',
                        '& label': {
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          width: '90%',
                          fontSize: '16px',
                          transform: 'translate(14px, 16px) scale(1)',
                          '&.MuiInputLabel-shrink': {
                            overflow: 'unset',
                          },
                        },
                        '& input': {
                          height: '48px',
                          fontSize: '16px',
                          padding: '0 14px',
                        },
                      }}
                    />
                    {form.errors.signature && (
                      <FormHelperText error={true}>
                        {form.errors.signature}
                      </FormHelperText>
                    )}
                  </Box>
                </Grid>
                <Box
                  css={{
                    display: 'flex',
                    gap: '32px',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    marginTop: '24px',
                  }}
                >
                  <Button
                    variant="outlined"
                    color="primary"
                    className={classes.button}
                    onClick={() => navigate(ROUTES.ACCOUNT)}
                  >
                    {content.get('REUSABLE_CANCEL')}
                  </Button>
                  <Button
                    type={'submit'}
                    className={classes.button}
                    css={{
                      backgroundColor: colors.buttonHoverPrimary,
                      border: `1px solid ${colors.buttonHoverPrimary}`,
                      color: colors.white,
                      '&:hover': {
                        backgroundColor: colors.buttonHoverPrimary,
                        borderColor: colors.buttonHoverPrimary,
                        color: colors.white,
                      },
                    }}
                  >
                    {headercardData?.type === IQBD_HEADER_CARD_TYPES.Enrolled ||
                    headercardData?.type === IQBD_HEADER_CARD_TYPES.Success
                      ? content.get('ENROLL')
                      : content.get('SUBMIT')}
                  </Button>
                </Box>
              </>
            )}
          </Grid>
          <HouseholdIncomeGuidelines
            isOpen={showHouseholdIncomeGuidelines}
            content={content}
            handleClose={() => setShowHouseholdIncomeGuidelines(false)}
          />
        </Page2ColumnLayout>
      </form>
    </>
  );
};

export default withWidth()(Form);
